import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../api.js';
import { AlertCircle, Building, X } from 'lucide-react';

function DriverOrgTransferModal({ isOpen, onClose, onSuccess, onMessage, driver, organizations }) {
  const { t } = useTranslation();
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (driver && isOpen) {
      setSelectedOrgId(driver.org_id || '');
    }
  }, [driver, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrgId) {
      onMessage && onMessage({
        title: t('drivers.messages.errorTitle'),
        message: t('drivers.orgTransferModal.selectOrgRequired'),
        type: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      // Transfer driver to new organization
      // IMPORTANT: When transferring, also unassign from current vehicle
      const updateData = {
        org_id: selectedOrgId
      };

      await api.driver.update(driver.id, updateData);

      // If driver has an assigned vehicle, unassign it
      // (This is handled by finding vehicles assigned to this driver and clearing assigned_driver_id)
      if (driver.assigned_vehicle_id) {
        try {
          await api.vehicle.update(driver.assigned_vehicle_id, { assigned_driver_id: null });
        } catch (vehicleError) {
          console.error('Error unassigning vehicle:', vehicleError);
          // Continue anyway - driver was transferred
        }
      }

      onMessage && onMessage({
        title: t('drivers.messages.successTitle'),
        message: t('drivers.orgTransferModal.transferSuccess'),
        type: 'success'
      });
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error transferring driver:', error);
      onMessage && onMessage({
        title: t('drivers.messages.errorTitle'),
        message: t('drivers.orgTransferModal.transferError'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentOrg = organizations.find(o => o.code === driver?.org_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="bg-blue-500 text-white px-6 py-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold">{t('drivers.orgTransferModal.title')}</h3>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-600 mb-1">{t('drivers.orgTransferModal.driver')}</p>
              <p className="font-semibold text-gray-900">{driver?.first_name} {driver?.last_name}</p>
              <p className="text-sm text-gray-600">{driver?.email}</p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="text-sm text-gray-600 mb-1">{t('drivers.orgTransferModal.currentOrg')}</p>
              <p className="font-semibold text-gray-900">{currentOrg?.name || t('drivers.orgTransferModal.notAssigned')}</p>
              {currentOrg && (
                <p className="text-xs text-gray-600">{t('drivers.orgTransferModal.code', { code: currentOrg.code })}</p>
              )}
            </div>

            {driver?.assigned_vehicle_id && (
              <div className="bg-amber-50 p-3 rounded-lg mb-4 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">{t('drivers.orgTransferModal.warningTitle')}</p>
                  <p>{t('drivers.orgTransferModal.warningUnassign')}</p>
                </div>
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('drivers.orgTransferModal.newOrg')} *
            </label>
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t('drivers.orgTransferModal.selectOrgPlaceholder')}</option>
              {organizations.map(org => (
                <option key={org.id} value={org.code}>
                  {org.name} ({org.code})
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              disabled={loading}
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('drivers.orgTransferModal.transferring')}
                </>
              ) : (
                <>
                  <Building className="w-4 h-4" />
                  {t('drivers.orgTransferModal.transfer')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// --- DRIVERS LIST COMPONENT ---

export default DriverOrgTransferModal;
